import { scopedStorageKey } from "@/lib/user-scope";

export type PoolStatus = "open" | "queued" | "negotiation" | "bidding" | "confirmed" | "complete";

export type PoolCommitment = {
  id: string;
  providerSlug: string;
  providerName: string;
  hardwareId: string;
  hardwareLabel: string;
  gpuCount: number;
  durationDays: number;
  gpuHours: number;
  intendedTasks: string[];
  current?: {
    providerSlug?: string;
    providerName?: string;
    hardwareId?: string;
    hardwareLabel?: string;
    gpuCount?: number;
    contractDaysLeft?: number;
    jobTypes?: string[];
  };
  status: PoolStatus;
  termsAccepted?: boolean;
  termsAcceptedAt?: string;
  createdAt: string;
  createdBy?: {
    userId?: string;
    email?: string;
    name?: string;
  };
};

export type PooledComputeResponse = {
  commitments: PoolCommitment[];
};

export const POOLED_COMPUTE_API = "/api/wallet/accounts?action=pooled_compute";
const POOLED_COMPUTE_CACHE_KEY = "genrl.pooled-compute.commitments.v1";

export function normalizeCommitments(value: unknown): PoolCommitment[] {
  const rows = Array.isArray(value) ? value : [];
  return rows
    .map((item: any): PoolCommitment => {
      const gpuCount = Number(item?.gpuCount || 0);
      const durationDays = Number(item?.durationDays || 0);
      return {
        id: String(item?.id || ""),
        providerSlug: String(item?.providerSlug || "open_pool"),
        providerName: String(item?.providerName || ""),
        hardwareId: String(item?.hardwareId || item?.hardwareLabel || "hardware"),
        hardwareLabel: String(item?.hardwareLabel || item?.hardwareId || "Hardware"),
        gpuCount,
        durationDays,
        gpuHours: Number(item?.gpuHours || gpuCount * durationDays * 24 || 0),
        intendedTasks: Array.isArray(item?.intendedTasks) ? item.intendedTasks.map(String).filter(Boolean) : [],
        current: item?.current && typeof item.current === "object"
          ? {
            providerSlug: String(item.current.providerSlug || ""),
            providerName: String(item.current.providerName || ""),
            hardwareId: String(item.current.hardwareId || ""),
            hardwareLabel: String(item.current.hardwareLabel || ""),
            gpuCount: Number(item.current.gpuCount || 0),
            contractDaysLeft: Number(item.current.contractDaysLeft || 0),
            jobTypes: Array.isArray(item.current.jobTypes) ? item.current.jobTypes.map(String).filter(Boolean) : [],
          }
          : undefined,
        status: normalizePoolStatus(item?.status),
        termsAccepted: item?.termsAccepted === true,
        termsAcceptedAt: String(item?.termsAcceptedAt || ""),
        createdAt: String(item?.createdAt || ""),
        createdBy: item?.createdBy && typeof item.createdBy === "object"
          ? {
            userId: String(item.createdBy.userId || ""),
            email: String(item.createdBy.email || ""),
            name: String(item.createdBy.name || ""),
          }
          : undefined,
      };
    })
    .filter((item) => item.id);
}

export function normalizePoolStatus(value: unknown): PoolStatus {
  if (value === "open" || value === "queued" || value === "negotiation" || value === "bidding" || value === "confirmed" || value === "complete") return value;
  if (value === "active") return "confirmed";
  return "queued";
}

function commitmentSignature(item: PoolCommitment): string {
  return [
    item.providerSlug,
    item.hardwareId,
    item.hardwareLabel,
    item.gpuCount,
    item.durationDays,
    [...(item.intendedTasks || [])].sort().join(","),
  ].map((part) => String(part || "").trim().toLowerCase()).join("|");
}

function isLocalCommitment(item: PoolCommitment): boolean {
  return String(item.id || "").startsWith("local_");
}

export function mergeCommitments(...groups: PoolCommitment[][]): PoolCommitment[] {
  const byID = new Map<string, PoolCommitment>();
  const localBySignature = new Map<string, string>();
  const serverSignatures = new Set<string>();

  for (const group of groups) {
    for (const item of group) {
      if (!item?.id) continue;
      const signature = commitmentSignature(item);
      if (byID.has(item.id)) continue;

      if (isLocalCommitment(item)) {
        if (serverSignatures.has(signature)) continue;
        if (localBySignature.has(signature)) continue;
        localBySignature.set(signature, item.id);
      } else {
        serverSignatures.add(signature);
        const localID = localBySignature.get(signature);
        if (localID) {
          byID.delete(localID);
          localBySignature.delete(signature);
        }
      }

      byID.set(item.id, item);
    }
  }

  return [...byID.values()].sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
}

export function readCachedCommitments(): PoolCommitment[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(scopedStorageKey(POOLED_COMPUTE_CACHE_KEY));
    if (!raw) return [];
    return normalizeCommitments(JSON.parse(raw));
  } catch {
    return [];
  }
}

export function writeCachedCommitments(commitments: PoolCommitment[]): void {
  if (typeof window === "undefined") return;
  try {
    const rows = normalizeCommitments(commitments).slice(0, 50);
    window.localStorage.setItem(scopedStorageKey(POOLED_COMPUTE_CACHE_KEY), JSON.stringify(rows));
  } catch {
    // Cache is only a same-browser display fallback after a successful backend write.
  }
}

export function removeCachedCommitment(id: string): PoolCommitment[] {
  const next = readCachedCommitments().filter((item) => item.id !== id);
  writeCachedCommitments(next);
  return next;
}
